// client/src/pages/ReportsPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container, Card, Alert, Button, Badge, Form,
  Table, Modal, Spinner,
} from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import { getAuthHeaders } from '../utils/authHelpers';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const REPORTS_URL  = `${API_ROOT_URL}/reports`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Return the Monday of the week containing `date` as a YYYY-MM-DD string. */
function getMondayString(date) {
  const d    = new Date(date);
  const day  = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon  = new Date(d.getFullYear(), d.getMonth(), diff);
  return mon.toISOString().split('T')[0];
}

/** Build an array of Monday strings from `start` through `end` (inclusive). */
function buildWeekOptions(start, end) {
  const weeks   = [];
  const current = new Date(getMondayString(start) + 'T00:00:00');
  const stop    = new Date(getMondayString(end)   + 'T00:00:00');
  while (current <= stop) {
    weeks.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 7);
  }
  return weeks;
}

/** Format a date value safely for display. */
function fmtDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Format a Monday string as a readable week label. */
function fmtWeek(weekStart) {
  const d = new Date(weekStart + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ─── Print styles ─────────────────────────────────────────────────────────────
const PRINT_STYLE = `
@media print {
  @page { margin: 0.75in; size: auto; }
  html, body { height: auto !important; overflow: visible !important; }
  body * { visibility: hidden !important; }
  #report-print-area, #report-print-area * { visibility: visible !important; }
  #report-print-area {
    position: absolute; top: 0; left: 0;
    width: 100%; height: auto !important;
    overflow: visible !important; background: white; padding: 0;
  }
  #report-print-area .table {
    page-break-inside: auto; font-size: 11pt;
    table-layout: auto !important; width: 100% !important;
  }
  #report-print-area .table tr { page-break-inside: avoid; }
  #report-print-area .table td,
  #report-print-area .table th {
    white-space: normal !important; overflow: visible !important;
    text-overflow: unset !important; max-width: none !important;
    word-break: break-word;
  }
}
`;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReportsPage() {
  const { user, allOrders, loading: ordersLoading, fetchOrdersForRole } = useDashboardData();

  const isManufacturing = user?.role === 'Manufacturing';

  // ── Global state ──────────────────────────────────────────────────────────
  const [reports,        setReports]        = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [alert,          setAlert]          = useState({ message: '', type: '' });

  // ── Section 1: Find an Order ──────────────────────────────────────────────
  const [orderSearchTerm,    setOrderSearchTerm]    = useState('');
  const [orderSearchResults, setOrderSearchResults] = useState([]);
  const [orderSearchLoading, setOrderSearchLoading] = useState(false);
  const [orderSearchDone,    setOrderSearchDone]    = useState(false);
  const reportDetailCache = useRef({});

  // ── Section 2: All Reports — year/month filter ────────────────────────────
  const [selectedYear,  setSelectedYear]  = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // ── Viewing a report ──────────────────────────────────────────────────────
  const [viewReport,        setViewReport]        = useState(null);
  const [viewReportLoading, setViewReportLoading] = useState(false);
  const [copyStatus,        setCopyStatus]        = useState('idle');

  // ── Edit mode ─────────────────────────────────────────────────────────────
  // orderMoves: { [orderId]: targetWeekStart } — only populated for orders
  // the user has chosen to move. Orders not in this map stay in their report.
  const [editingReport, setEditingReport] = useState(null);
  const [orderMoves,    setOrderMoves]    = useState({});   // orderId → weekStart
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updating,        setUpdating]        = useState(false);

  // ── Visibility ────────────────────────────────────────────────────────────
  const [togglingVisibility, setTogglingVisibility] = useState(null);

  // ── Silent auto-sync state ────────────────────────────────────────────────
  // True while the background sync is running — used to show a subtle spinner
  // in the header rather than blocking the UI.
  const [syncing, setSyncing] = useState(false);
  // Ref guard: prevents a second sync from starting before the first finishes,
  // even if eligibleOrders/reports both change in the same render cycle.
  const syncInFlight = useRef(false);

  // ── Inject print CSS once ─────────────────────────────────────────────────
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = PRINT_STYLE;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ── Auto-dismiss alerts ───────────────────────────────────────────────────
  useEffect(() => {
    if (!alert.message) return;
    const t = setTimeout(() => setAlert({ message: '', type: '' }), 4000);
    return () => clearTimeout(t);
  }, [alert.message]);

  // ── Fetch report list ─────────────────────────────────────────────────────
  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const res  = await fetch(REPORTS_URL, { headers: getAuthHeaders() });
      const data = await res.json();
      if (res.ok) setReports(data);
      else setAlert({ message: data.message || 'Failed to load reports.', type: 'danger' });
    } catch {
      setAlert({ message: 'Network error loading reports.', type: 'danger' });
    } finally {
      setReportsLoading(false);
    }
  }, []);

  useEffect(() => { if (user) fetchReports(); }, [fetchReports, user]);

  // ── Week options ──────────────────────────────────────────────────────────
  const weekOptions = useMemo(() => {
    const earliest = new Date();
    earliest.setFullYear(earliest.getFullYear() - 3);
    const latest = new Date();
    latest.setDate(latest.getDate() + 7 * 4);
    return buildWeekOptions(earliest, latest);
  }, []);

  // ── Year/month options for the All Reports filter ─────────────────────────
  const yearOptions = useMemo(() => {
    const fromReports = reports.map(r => new Date(r.weekStart + 'T00:00:00').getFullYear());
    const fromWeeks   = weekOptions.map(w => new Date(w + 'T00:00:00').getFullYear());
    return Array.from(new Set([...fromReports, ...fromWeeks])).sort((a, b) => a - b);
  }, [reports, weekOptions]);

  const monthOptions = useMemo(() => {
    const months = weekOptions
      .filter(w => new Date(w + 'T00:00:00').getFullYear() === selectedYear)
      .map(w => new Date(w + 'T00:00:00').getMonth() + 1);
    return Array.from(new Set(months)).sort((a, b) => a - b);
  }, [weekOptions, selectedYear]);

  useEffect(() => {
    if (monthOptions.length && !monthOptions.includes(selectedMonth)) {
      setSelectedMonth(monthOptions[monthOptions.length - 1]);
    }
  }, [monthOptions, selectedMonth]);

  // ── Visibility-filtered report list ──────────────────────────────────────
  const visibleReports = useMemo(
    () => isManufacturing ? reports : reports.filter(r => r.isVisible),
    [reports, isManufacturing]
  );

  // ── Reports for selected month ────────────────────────────────────────────
  const reportsForSelectedMonth = useMemo(() => {
    return visibleReports.filter(r => {
      const d = new Date(r.weekStart + 'T00:00:00');
      return d.getFullYear() === selectedYear && d.getMonth() + 1 === selectedMonth;
    });
  }, [visibleReports, selectedYear, selectedMonth]);

  // ── Fulfillment date helper ───────────────────────────────────────────────
  // Completed        → shippedDate ?? completionDate
  // Shipping+pallet  → shippedDate ?? completionDate ?? shipping.lastUpdated ?? updatedAt
  // (palletWrappedDate does not exist on the schema; lastUpdated is the
  //  guaranteed non-null fallback on the shipping sub-document)
  const getFulfillmentDate = useCallback((order) => {
    if (order.currentStage === 'Completed') {
      return order.shipping?.shippedDate || order.completionDate || null;
    }
    if (order.currentStage === 'Shipping' && order.shipping?.palletWrapped) {
      return (
        order.shipping?.shippedDate ||
        order.completionDate        ||
        order.shipping?.lastUpdated ||
        order.updatedAt             ||
        null
      );
    }
    return null;
  }, []);

  // ── All eligible orders ───────────────────────────────────────────────────
  // Every Completed order (any date) + Shipping orders with palletWrapped=true.
  // Must resolve a fulfillment date. Sorted oldest → newest.
  const eligibleOrders = useMemo(() => {
    if (!isManufacturing) return [];
    return allOrders
      .filter(o => {
        if (o.currentStage === 'Completed') return true;
        if (o.currentStage === 'Shipping' && o.shipping?.palletWrapped) return true;
        return false;
      })
      .filter(o => !!getFulfillmentDate(o))
      .sort((a, b) => new Date(getFulfillmentDate(a)) - new Date(getFulfillmentDate(b)));
  }, [allOrders, isManufacturing, getFulfillmentDate]);

  // ── Unassigned eligible orders ────────────────────────────────────────────
  // Used exclusively by the silent auto-sync effect below.
  const unassignedOrders = useMemo(
    () => eligibleOrders.filter(o => !o.isReported),
    [eligibleOrders]
  );

  // ── Silent auto-sync ──────────────────────────────────────────────────────
  // Runs automatically whenever unassigned eligible orders exist.
  // No user interaction required — orders flow into their week's report as
  // soon as both allOrders and the report list are available.
  // A ref guard prevents concurrent runs if the effect fires multiple times.
  const runAutoSync = useCallback(async (ordersToSync, currentReports) => {
    if (ordersToSync.length === 0 || syncInFlight.current) return;
    syncInFlight.current = true;
    setSyncing(true);
    try {
      // Group by fulfillment week
      const byWeek = {};
      ordersToSync.forEach(order => {
        const week = getMondayString(new Date(getFulfillmentDate(order)));
        if (!byWeek[week]) byWeek[week] = [];
        byWeek[week].push(order.orderId);
      });

      for (const [week, orderIds] of Object.entries(byWeek)) {
        const existing = currentReports.find(r => r.weekStart === week);
        if (existing) {
          // Merge new orders into the existing report
          const detailRes  = await fetch(`${REPORTS_URL}/${week}`, { headers: getAuthHeaders() });
          const detailData = await detailRes.json();
          const merged = [...new Set([
            ...(detailData.orders || []).map(o => o.orderId),
            ...orderIds,
          ])];
          await fetch(`${REPORTS_URL}/${week}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ orderIds: merged }),
          });
          delete reportDetailCache.current[week];
        } else {
          // Create a new report for this week
          await fetch(REPORTS_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ weekStart: week, orderIds }),
          });
        }
      }

      // Refresh both lists so the UI reflects the new state
      await Promise.all([fetchReports(), fetchOrdersForRole()]);
    } catch {
      // Silent failure — non-blocking, will retry on next render cycle
    } finally {
      setSyncing(false);
      syncInFlight.current = false;
    }
  }, [getFulfillmentDate, fetchReports, fetchOrdersForRole]);

  // Trigger auto-sync whenever there are unassigned orders and reports are loaded
  useEffect(() => {
    if (!isManufacturing) return;
    if (reportsLoading || ordersLoading) return;
    if (unassignedOrders.length === 0) return;
    runAutoSync(unassignedOrders, reports);
  }, [unassignedOrders, reports, reportsLoading, ordersLoading, isManufacturing, runAutoSync]);

  // ── Section 1: order search across all reports ────────────────────────────
  const handleOrderSearch = useCallback(async (rawTerm) => {
    const term = rawTerm.trim().toLowerCase();
    if (!term) { setOrderSearchResults([]); setOrderSearchDone(false); return; }
    setOrderSearchLoading(true);
    setOrderSearchDone(false);
    try {
      await Promise.all(
        visibleReports.map(async (r) => {
          if (reportDetailCache.current[r.weekStart]) return;
          const res  = await fetch(`${REPORTS_URL}/${r.weekStart}`, { headers: getAuthHeaders() });
          const data = await res.json();
          if (res.ok) reportDetailCache.current[r.weekStart] = data;
        })
      );
      const hits = [];
      visibleReports.forEach(r => {
        const detail = reportDetailCache.current[r.weekStart];
        if (!detail) return;
        const matched = detail.orders.filter(o =>
          (o.customerName  || '').toLowerCase().includes(term) ||
          (o.invoiceNumber || '').toLowerCase().includes(term)
        );
        if (matched.length > 0) hits.push({ report: detail, matchedOrders: matched });
      });
      setOrderSearchResults(hits);
    } catch {
      setAlert({ message: 'Network error during order search.', type: 'danger' });
    } finally {
      setOrderSearchLoading(false);
      setOrderSearchDone(true);
    }
  }, [visibleReports]);

  // ── View a report ─────────────────────────────────────────────────────────
  const handleViewReport = useCallback(async (weekStart) => {
    // Close any open edit session first
    setEditingReport(null);
    setOrderMoves({});
    setViewReportLoading(true);
    try {
      const res  = await fetch(`${REPORTS_URL}/${weekStart}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (res.ok) setViewReport(data);
      else setAlert({ message: data.message || 'Failed to load report.', type: 'danger' });
    } catch {
      setAlert({ message: 'Network error loading report.', type: 'danger' });
    } finally {
      setViewReportLoading(false);
    }
  }, []);

  const handleSearchResultClick = (weekStart) => {
    const d = new Date(weekStart + 'T00:00:00');
    setSelectedYear(d.getFullYear());
    setSelectedMonth(d.getMonth() + 1);
    setOrderSearchTerm('');
    setOrderSearchResults([]);
    setOrderSearchDone(false);
    handleViewReport(weekStart);
  };

  // ── Enter edit mode ───────────────────────────────────────────────────────
  const handleEditReport = useCallback(async (weekStart) => {
    // Close any open view first
    setViewReport(null);
    try {
      const res  = await fetch(`${REPORTS_URL}/${weekStart}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (res.ok) {
        setEditingReport(data);
        setOrderMoves({});
      } else {
        setAlert({ message: data.message || 'Could not load report for editing.', type: 'danger' });
      }
    } catch {
      setAlert({ message: 'Network error loading report for editing.', type: 'danger' });
    }
  }, []);

  const handleCancelEdit = () => { setEditingReport(null); setOrderMoves({}); };

  // Set or clear the target week for a given order
  const setOrderMove = (orderId, targetWeek) => {
    setOrderMoves(prev => {
      const next = { ...prev };
      if (!targetWeek) {
        delete next[orderId]; // cleared back to "keep"
      } else {
        next[orderId] = targetWeek;
      }
      return next;
    });
  };

  // ── Update report: apply all pending moves ────────────────────────────────
  // For each order marked to move:
  //   1. Remove it from the current report (PUT with that orderId excluded)
  //   2. Add it to the target report (PUT existing, or POST new)
  // Orders with no move entry are untouched.
  const handleUpdate = useCallback(async () => {
    setShowUpdateModal(false);
    const moveEntries = Object.entries(orderMoves); // [[orderId, targetWeek], …]
    if (moveEntries.length === 0) {
      setAlert({ message: 'No moves to apply.', type: 'info' });
      return;
    }
    setUpdating(true);
    try {
      const movedOrderIds   = new Set(moveEntries.map(([id]) => id));
      const currentOrderIds = (editingReport.orders || []).map(o => o.orderId);

      // Step 1: Remove moved orders from the source report
      const remainingIds = currentOrderIds.filter(id => !movedOrderIds.has(id));
      await fetch(`${REPORTS_URL}/${editingReport.weekStart}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ orderIds: remainingIds }),
      });
      delete reportDetailCache.current[editingReport.weekStart];

      // Step 2: Group moved orders by their target week and upsert each report
      const byTargetWeek = {};
      moveEntries.forEach(([orderId, targetWeek]) => {
        if (!byTargetWeek[targetWeek]) byTargetWeek[targetWeek] = [];
        byTargetWeek[targetWeek].push(orderId);
      });

      for (const [targetWeek, orderIds] of Object.entries(byTargetWeek)) {
        const existing = reports.find(r => r.weekStart === targetWeek);
        if (existing) {
          const detailRes  = await fetch(`${REPORTS_URL}/${targetWeek}`, { headers: getAuthHeaders() });
          const detailData = await detailRes.json();
          const merged = [...new Set([
            ...(detailData.orders || []).map(o => o.orderId),
            ...orderIds,
          ])];
          await fetch(`${REPORTS_URL}/${targetWeek}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ orderIds: merged }),
          });
          delete reportDetailCache.current[targetWeek];
        } else {
          await fetch(REPORTS_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ weekStart: targetWeek, orderIds }),
          });
        }
      }

      const movedCount = moveEntries.length;
      setAlert({ message: `${movedCount} order${movedCount !== 1 ? 's' : ''} moved successfully.`, type: 'success' });
      setEditingReport(null);
      setOrderMoves({});
      await Promise.all([fetchReports(), fetchOrdersForRole()]);
    } catch {
      setAlert({ message: 'Network error updating report.', type: 'danger' });
    } finally {
      setUpdating(false);
    }
  }, [editingReport, orderMoves, reports, fetchReports, fetchOrdersForRole]);

  // ── Toggle visibility ─────────────────────────────────────────────────────
  const handleToggleVisibility = useCallback(async (report) => {
    setTogglingVisibility(report.weekStart);
    try {
      const res  = await fetch(`${REPORTS_URL}/${report.weekStart}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ isVisible: !report.isVisible }),
      });
      const data = await res.json();
      if (res.ok) {
        setAlert({
          message: data.report.isVisible
            ? 'Report is now visible to all roles.'
            : 'Report is now hidden from other roles.',
          type: 'info',
        });
        await fetchReports();
        if (viewReport?.weekStart === report.weekStart) {
          setViewReport(prev => ({ ...prev, isVisible: data.report.isVisible }));
        }
      } else {
        setAlert({ message: data.message || 'Failed to update visibility.', type: 'danger' });
      }
    } catch {
      setAlert({ message: 'Network error updating visibility.', type: 'danger' });
    } finally {
      setTogglingVisibility(null);
    }
  }, [viewReport, fetchReports]);

  // ── Copy to clipboard (TSV for Excel) ────────────────────────────────────
  const handleCopy = useCallback(() => {
    if (!viewReport) return;
    const headers = ['#', 'Invoice #', 'Order Type', 'Dealer', 'Customer', 'Shipping Method', 'Revenue', 'Fulfilled Date'];
    const rows = viewReport.orders.map((order, idx) => [
      idx + 1,
      order.invoiceNumber  || '',
      order.orderType      || '',
      order.dealerName     || '',
      order.customerName   || '',
      order.shippingMethod || '',
      Number(order.revenue) || 0,
      fmtDate(order.fulfilledDate),
    ]);
    rows.push(['', '', '', '', '', 'Weekly Total', viewReport.totalRevenue, '']);
    const tsv = [headers, ...rows]
      .map(row => row.map(cell => String(cell).replace(/\t/g, ' ')).join('\t'))
      .join('\n');
    navigator.clipboard.writeText(tsv)
      .then(() => { setCopyStatus('copied'); setTimeout(() => setCopyStatus('idle'), 2000); })
      .catch(() => { setCopyStatus('error');  setTimeout(() => setCopyStatus('idle'), 2000); });
  }, [viewReport]);

  const handlePrint = () => window.print();

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!user) return <Navigate to="/login" replace />;

  // ── Edit mode derived values ──────────────────────────────────────────────
  // Flat list of orders in the report being edited.
  const editablePool = (editingReport?.orders || []).map(o => ({
    orderId:        o.orderId,
    invoiceNumber:  o.invoiceNumber,
    orderType:      o.orderType,
    dealerName:     o.dealerName,
    customerName:   o.customerName,
    shippingMethod: o.shippingMethod,
    revenue:        o.revenue,
    _fulfilledDate: o.fulfilledDate,
  }));

  // Week options available as move targets: all report weeks except the one
  // being edited, sorted chronologically.
  const moveTargetWeeks = visibleReports
    .filter(r => r.weekStart !== editingReport?.weekStart)
    .map(r => r.weekStart)
    .sort()
    .reverse();

  const pendingMoveCount = Object.keys(orderMoves).length;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container fluid className="bg-light min-vh-100 py-4">
      <Container>

        {/* ── Header ── */}
        <Card className="mb-4 shadow-sm text-center">
          <Card.Body>
            <h2 className="mb-0">
              <i className="bi bi-file-earmark-bar-graph me-2" />
              Weekly Production Reports
              {syncing && (
                <Spinner
                  size="sm"
                  animation="border"
                  variant="secondary"
                  className="ms-2"
                  title="Syncing new orders into reports…"
                />
              )}
            </h2>
            <p className="text-muted mb-0 mt-1">
              {isManufacturing
                ? 'Reports are generated automatically as orders are fulfilled.'
                : 'View and print finalized weekly production reports.'}
            </p>
          </Card.Body>
        </Card>

        {alert.message && <Alert variant={alert.type}>{alert.message}</Alert>}

        {/* ══════════════════════════════════════════════════════════════════
            SECTION 1 — FIND AN ORDER
        ══════════════════════════════════════════════════════════════════ */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-white py-3">
            <h5 className="mb-0">
              <i className="bi bi-search me-2" />
              Find an Order
            </h5>
          </Card.Header>
          <Card.Body>
            <div className="d-flex gap-2 mb-3">
              <Form.Control
                placeholder="Search by Customer Name or Invoice Number…"
                value={orderSearchTerm}
                onChange={e => {
                  setOrderSearchTerm(e.target.value);
                  setOrderSearchResults([]);
                  setOrderSearchDone(false);
                }}
                onKeyDown={e => { if (e.key === 'Enter') handleOrderSearch(orderSearchTerm); }}
              />
              <Button
                variant="outline-primary"
                onClick={() => handleOrderSearch(orderSearchTerm)}
                disabled={orderSearchLoading || !orderSearchTerm.trim()}
                style={{ whiteSpace: 'nowrap' }}
              >
                {orderSearchLoading
                  ? <><Spinner size="sm" className="me-1" />Searching…</>
                  : <><i className="bi bi-search me-1" />Search</>
                }
              </Button>
            </div>

            {orderSearchDone && (
              orderSearchResults.length === 0 ? (
                <p className="text-muted text-center py-3 mb-0">
                  No orders found matching <strong>"{orderSearchTerm}"</strong>.
                </p>
              ) : (
                <div className="border rounded bg-white shadow-sm" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {orderSearchResults.map(({ report, matchedOrders }) => (
                    <div
                      key={report.weekStart}
                      className="px-3 py-2 border-bottom"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSearchResultClick(report.weekStart)}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-semibold">
                          <i className="bi bi-file-earmark-text me-1 text-primary" />
                          Week of {fmtWeek(report.weekStart)}
                        </span>
                        <Badge bg="primary" pill>
                          {matchedOrders.length} match{matchedOrders.length !== 1 ? 'es' : ''}
                        </Badge>
                      </div>
                      {matchedOrders.map(o => (
                        <div key={o.orderId} className="d-flex gap-3 ps-2 small text-muted">
                          <span><i className="bi bi-receipt me-1" />{o.invoiceNumber || '—'}</span>
                          <span><i className="bi bi-person me-1" />{o.customerName   || '—'}</span>
                          <span className="text-success fw-semibold">${Number(o.revenue).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )
            )}

            {!orderSearchDone && !orderSearchLoading && (
              <p className="text-muted small mb-0">
                Enter a customer name or invoice number and press Search.
              </p>
            )}
          </Card.Body>
        </Card>

        {/* ══════════════════════════════════════════════════════════════════
            SECTION 2 — ALL REPORTS (filtered by year + month)
        ══════════════════════════════════════════════════════════════════ */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-white py-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="mb-0">
                <i className="bi bi-archive me-2" />
                All Reports
                <Badge bg="secondary" className="ms-2">{visibleReports.length}</Badge>
              </h5>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  style={{ width: 'auto', minWidth: 100 }}
                  value={selectedYear}
                  onChange={e => setSelectedYear(Number(e.target.value))}
                >
                  {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                </Form.Select>
                <Form.Select
                  style={{ width: 'auto', minWidth: 140 }}
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(Number(e.target.value))}
                >
                  {monthOptions.map(m => (
                    <option key={m} value={m}>
                      {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </Form.Select>
                <span className="text-muted small text-nowrap">
                  {reportsForSelectedMonth.length} report{reportsForSelectedMonth.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {reportsLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading reports…</p>
              </div>
            ) : visibleReports.length === 0 ? (
              <p className="text-center text-muted py-5 mb-0">No reports yet.</p>
            ) : reportsForSelectedMonth.length === 0 ? (
              <p className="text-center text-muted py-5 mb-0">
                No reports for{' '}
                {new Date(0, selectedMonth - 1).toLocaleString('default', { month: 'long' })} {selectedYear}.
              </p>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Week</th>
                      <th className="text-center">Orders</th>
                      <th className="text-center">Total Revenue</th>
                      {isManufacturing && <th className="text-center">Visibility</th>}
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsForSelectedMonth.map(report => (
                      <tr key={report.weekStart} className={
                        report.weekStart === viewReport?.weekStart ? 'table-primary' :
                        report.weekStart === editingReport?.weekStart ? 'table-warning' : ''
                      }>
                        <td className="fw-semibold">Week of {fmtWeek(report.weekStart)}</td>
                        <td className="text-center">
                          <Badge bg="secondary">{report.orderCount}</Badge>
                        </td>
                        <td className="text-center fw-bold text-success">
                          ${report.totalRevenue.toLocaleString()}
                        </td>
                        {isManufacturing && (
                          <td className="text-center">
                            <Badge bg={report.isVisible ? 'success' : 'secondary'}>
                              {report.isVisible ? 'Visible' : 'Hidden'}
                            </Badge>
                          </td>
                        )}
                        <td className="text-center">
                          <div className="d-flex gap-2 justify-content-center">
                            <Button
                              size="sm" variant="outline-primary"
                              disabled={viewReportLoading}
                              onClick={() => handleViewReport(report.weekStart)}
                            >
                              <i className="bi bi-eye me-1" />View
                            </Button>
                            {isManufacturing && (
                              <>
                                <Button size="sm" variant="outline-warning" onClick={() => handleEditReport(report.weekStart)}>
                                  <i className="bi bi-pencil me-1" />Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant={report.isVisible ? 'outline-secondary' : 'outline-success'}
                                  disabled={togglingVisibility === report.weekStart}
                                  onClick={() => handleToggleVisibility(report)}
                                  title={report.isVisible ? 'Hide from other roles' : 'Make visible to all roles'}
                                >
                                  {togglingVisibility === report.weekStart
                                    ? <Spinner size="sm" />
                                    : report.isVisible
                                      ? <><i className="bi bi-eye-slash me-1" />Hide</>
                                      : <><i className="bi bi-eye me-1" />Publish</>
                                  }
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ══════════════════════════════════════════════════════════════════
            SECTION 3 — FULL REPORT VIEW
        ══════════════════════════════════════════════════════════════════ */}
        {viewReport && (
          <Card className="mb-4 shadow-sm" id="report-print-area">
            <Card.Header className="bg-white py-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="mb-0">
                    <i className="bi bi-file-earmark-text me-2" />
                    Report — Week of {fmtWeek(viewReport.weekStart)}
                  </h5>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <Badge bg="secondary" className="fs-6">{viewReport.orderCount} orders</Badge>
                  <span className="fw-bold text-success fs-5">${viewReport.totalRevenue.toLocaleString()}</span>
                  <Button
                    variant={copyStatus === 'copied' ? 'outline-success' : copyStatus === 'error' ? 'outline-danger' : 'outline-secondary'}
                    size="sm" onClick={handleCopy} title="Copy report to clipboard (paste into Excel)"
                  >
                    {copyStatus === 'copied'
                      ? <><i className="bi bi-check-lg me-1" />Copied!</>
                      : copyStatus === 'error'
                      ? <><i className="bi bi-x-lg me-1" />Failed</>
                      : <><i className="bi bi-copy me-1" />Copy</>
                    }
                  </Button>
                  <Button variant="outline-dark" size="sm" onClick={handlePrint}>
                    <i className="bi bi-printer me-1" />Print
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => setViewReport(null)}>
                    <i className="bi bi-x-lg" />
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table bordered className="mb-0 align-middle bg-white">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Invoice #</th>
                      <th>Order Type</th>
                      <th>Dealer</th>
                      <th>Customer</th>
                      <th>Shipping Method</th>
                      <th className="text-end">Revenue</th>
                      <th>Fulfilled Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewReport.orders.map((order, idx) => (
                      <tr key={order.orderId}>
                        <td className="text-muted">{idx + 1}</td>
                        <td>{order.invoiceNumber  || '—'}</td>
                        <td>{order.orderType      || '—'}</td>
                        <td>{order.dealerName     || '—'}</td>
                        <td>{order.customerName   || '—'}</td>
                        <td>{order.shippingMethod || '—'}</td>
                        <td className="text-end fw-bold text-success">
                          ${Number(order.revenue).toLocaleString()}
                        </td>
                        <td>{fmtDate(order.fulfilledDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan={6} className="fw-bold text-end">Weekly Total</td>
                      <td className="fw-bold text-success text-end">${viewReport.totalRevenue.toLocaleString()}</td>
                      <td />
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            SECTION 4 — EDIT REPORT (Manufacturing only)
            Use the "Move To" dropdown on any row to reassign that order to a
            different week's report. Click Update Report to apply.
        ══════════════════════════════════════════════════════════════════ */}
        {isManufacturing && editingReport && (
          <Card className="mb-4 shadow-sm border-warning">
            <Card.Header className="bg-warning bg-opacity-10 py-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="mb-0 text-warning-emphasis">
                    <i className="bi bi-pencil-square me-2" />
                    Editing Report — Week of {fmtWeek(editingReport.weekStart)}
                  </h5>
                  <small className="text-muted">
                    Use the <strong>Move To</strong> column to reassign an order to a different week. Changes are not saved until you click <strong>Update Report</strong>.
                  </small>
                </div>
                <Button variant="outline-secondary" size="sm" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {editablePool.length === 0 ? (
                <Alert variant="info" className="mb-0">This report has no orders.</Alert>
              ) : (
                <>
                  <div className="table-responsive rounded shadow-sm">
                    <Table bordered hover size="sm" className="mb-0 bg-white align-middle">
                      <thead className="table-dark">
                        <tr>
                          <th>Invoice #</th>
                          <th>Order Type</th>
                          <th>Dealer</th>
                          <th>Customer</th>
                          <th>Shipping Method</th>
                          <th>Revenue</th>
                          <th>Fulfilled Date</th>
                          <th style={{ minWidth: 220 }}>Move To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editablePool.map(order => {
                          const hasMoved = !!orderMoves[order.orderId];
                          return (
                            <tr
                              key={order.orderId}
                              className={hasMoved ? 'table-warning' : ''}
                            >
                              <td>{order.invoiceNumber  || '—'}</td>
                              <td>{order.orderType      || '—'}</td>
                              <td>{order.dealerName     || '—'}</td>
                              <td>{order.customerName   || '—'}</td>
                              <td>{order.shippingMethod || '—'}</td>
                              <td className="fw-bold text-success">
                                {order.revenue ? `$${Number(order.revenue).toLocaleString()}` : '$0'}
                              </td>
                              <td>{fmtDate(order._fulfilledDate)}</td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={orderMoves[order.orderId] || ''}
                                  onChange={e => setOrderMove(order.orderId, e.target.value || null)}
                                >
                                  <option value="">— Keep in this report —</option>
                                  {moveTargetWeeks.map(w => (
                                    <option key={w} value={w}>Week of {fmtWeek(w)}</option>
                                  ))}
                                </Form.Select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <div>
                      {pendingMoveCount > 0 ? (
                        <Badge bg="warning" text="dark" className="fs-6">
                          {pendingMoveCount} order{pendingMoveCount !== 1 ? 's' : ''} pending move
                        </Badge>
                      ) : (
                        <span className="text-muted small">No moves selected.</span>
                      )}
                    </div>
                    <Button
                      variant="warning"
                      disabled={pendingMoveCount === 0 || updating}
                      onClick={() => setShowUpdateModal(true)}
                    >
                      {updating
                        ? <><Spinner size="sm" className="me-1" />Saving…</>
                        : <><i className="bi bi-arrow-repeat me-1" />Update Report</>
                      }
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {/* ── Update Report Confirm Modal ── */}
        <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered>
          <Modal.Header closeButton className="bg-warning">
            <Modal.Title>Confirm Report Update</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <i className="bi bi-arrow-repeat text-warning" style={{ fontSize: '2rem' }} />
            <p className="mt-3 mb-1">
              You are about to move <strong>{pendingMoveCount}</strong> order{pendingMoveCount !== 1 ? 's' : ''} out of{' '}
              <strong>Week of {editingReport ? fmtWeek(editingReport.weekStart) : '—'}</strong>.
            </p>
            <p className="text-muted small mb-0">
              Each order will be placed into its selected target report. This cannot be undone automatically.
            </p>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>Cancel</Button>
            <Button variant="warning" onClick={handleUpdate} disabled={updating}>Update Report</Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </Container>
  );
}